import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';

import groq from 'groq';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS, SHOW_NEWSLETTER_BLOCK } from '../../constants';
import { NavigationMenu } from '../../graphql-fragments/navigationMenu';
import logo from '../../images/heedCapitalLogo.svg';
import logoWhite from '../../images/heedCapitalLogo_white.svg';
import PreviewLoadingScreen from '../../preview/PreviewLoadingScreen';
import { usePreviewData } from '../../preview/previewUtils';
import { AreaOfExpertise } from '../../templates/AreaOfExpertisePage';
import { localize } from '../../utils/sanity';
import { SocialNetworksBlock } from './Footer';
import Layout from './Layout';

type SanityLayoutProps = {
  children: React.ReactNode;
  withFormAnchorLink?: boolean;
  hideNavigation?: boolean;
} & (
  | {
      heroRef: React.MutableRefObject<HTMLElement | null>;
      headerWithOpaqueBackground?: never;
    }
  | {
      heroRef?: never;
      headerWithOpaqueBackground: boolean;
    }
);

interface QueryData {
  sanityWebsite: {
    navigationMenu: NavigationMenu;
    contactsPage: {
      slug: {
        current: string;
      };
    };
    contacts: {
      address: string;
      email: string;
      phoneNumbers: Array<string>;
      whatsAppNumber?: string;
      calendlyLink: string;
    };
    socialMedia: {
      facebookUrl: string;
      linkedInUrl: string;
      twitterUrl: string;
      instagramUrl: string;
    };
  };
  allSanityLegalDocumentationPage: {
    totalCount: number;
    nodes: Array<{
      title: string;
      slug: {
        current: string;
      };
    }>;
  };
  allSanityAreaOfExpertise: {
    nodes: Array<AreaOfExpertise>;
  };
}

export interface CommonSettings {
  contacts: {
    calendlyLink: string;
  };
}

const SanityLayout = ({
  children,
  headerWithOpaqueBackground,
  heroRef,
  withFormAnchorLink,
  hideNavigation,
}: SanityLayoutProps): React.ReactElement => {
  const staticData = useStaticQuery<QueryData>(graphql`
    {
      sanityWebsite {
        navigationMenu {
          ...NavigationMenu
        }
        contactsPage {
          slug {
            current
          }
        }
        contacts {
          address {
            ...LocaleTextFragment
          }
          email
          phoneNumbers
          whatsAppNumber
          calendlyLink
        }
        socialMedia {
          facebookUrl
          linkedInUrl
          twitterUrl
          instagramUrl
        }
      }
      allSanityLegalDocumentationPage {
        totalCount
        nodes {
          title {
            ...LocaleStringFragment
          }
          slug {
            current
          }
        }
      }
      allSanityAreaOfExpertise(sort: { orderRank: ASC }) {
        nodes {
          ...AreaOfExpertise
        }
      }
    }
  `);
  const groqQuery = groq`{
    "sanityWebsite": *[_type == "website"][0] {
      ...
    }
  }`;

  const data = usePreviewData<QueryData>(staticData, {
    groqQuery,
  });

  if (!data) {
    return <PreviewLoadingScreen></PreviewLoadingScreen>;
  }

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: QueryData = localize(data, langs);

  const socialNetwork: SocialNetworksBlock = {
    title: t('footer.follow_social_media', 'Siga-nos'),
    socialNetworks: [
      { iconName: 'facebook', url: localizedData.sanityWebsite.socialMedia.facebookUrl },
      { iconName: 'linkedin', url: localizedData.sanityWebsite.socialMedia.linkedInUrl },
      { iconName: 'twitter', url: localizedData.sanityWebsite.socialMedia.twitterUrl },
      { iconName: 'instagram', url: localizedData.sanityWebsite.socialMedia.instagramUrl },
    ],
  };

  const legalDocsEntry = {
    title: t('footer.legal_documentation', 'Páginas legais'),
    docs: localizedData.allSanityLegalDocumentationPage.nodes,
  };

  return (
    <Layout
      heroRef={heroRef}
      hideNavigation={hideNavigation}
      headerConfig={{
        sticky: true,
        logoImage: logo,
        loogImageWhite: logoWhite,
        entries: localizedData.sanityWebsite.navigationMenu,
        areasOfExpertise: localizedData.allSanityAreaOfExpertise.nodes,
        withOpaqueBackground: headerWithOpaqueBackground,
        calendlyLink: localizedData.sanityWebsite.contacts.calendlyLink,
        withFormAnchorLink: withFormAnchorLink,
      }}
      footerConfig={{
        logoImage: logo,
        socialNetworksBlock: socialNetwork,
        legalDocsBlock:
          localizedData.allSanityLegalDocumentationPage.nodes.length > 0
            ? legalDocsEntry
            : undefined,
        newsletterBlock: SHOW_NEWSLETTER_BLOCK
          ? {
              title: t('footer.newsletter_title', 'Subscreva a nossa newsletter'),
              buttonText: t('footer.newsletter_button', 'Submeter'),
              emailId: 'email',
            }
          : undefined,
        contacts: {
          title: t('footer.contacts', 'Contactos'),
          address: localizedData.sanityWebsite.contacts.address,
          email: localizedData.sanityWebsite.contacts.email,
          phoneNumbers: localizedData.sanityWebsite.contacts.phoneNumbers,
          whatsAppNumber: localizedData.sanityWebsite.contacts.whatsAppNumber,
        },
      }}
    >
      {children}
    </Layout>
  );
};

export default SanityLayout;
